import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEventContainer } from '../../containers/EventContainer';
import { useSpotlight } from '../../stores/useSpotlight';
import { useApi } from '../../containers/ApiContainer';
import { useActiveFBOs } from '../../containers/ActiveFBOContainer';
import { ChecklistStep } from './types';
import { ACTIVATION_EVENTS } from './checklistSteps';

/**
 * Custom hook that handles checklist step clicks.
 * Extracted to eliminate code duplication across checklist variants.
 *
 * For 'trigger' actions: shows spotlight on button first (~1s), then emits event to open dialog
 * For 'navigate' actions: sets spotlight immediately then navigates
 *
 * If user is not on a hangar page, navigates to their sandbox hangar before action.
 */
export function useStepClickHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { emitEvent } = useEventContainer();
  const { setSpotlight } = useSpotlight();
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();

  // Check if currently on a hangar page
  const isOnHangarPage = /^\/hangars\/[^/]+/.test(location.pathname);

  return useCallback(
    async (step: ChecklistStep) => {
      const executeAction = () => {
        if (step.action?.type === 'trigger' && step.action.event) {
          // Build event data with optional prefill for add_aircraft step
          const eventData: { stepId: string; prefillTail?: string } = { stepId: step.id };
          if (step.action.event === ACTIVATION_EVENTS.ADD_AIRCRAFT) {
            eventData.prefillTail = 'N648AJ'; // Demo tail number to show autocomplete
          }

          // For dialog triggers: show spotlight on button FIRST, then emit event after delay
          // This lets user see the button flash before the dialog opens
          if (step.action?.spotlightId) {
            setSpotlight(step.action.spotlightId);
            // Delay event emission to let user see the button highlight (~1s)
            setTimeout(() => {
              emitEvent(step.action.event, { data: eventData });
            }, 1000);
          } else {
            // No spotlight - emit immediately
            emitEvent(step.action.event, { data: eventData });
          }
        } else if (step.action?.type === 'navigate' && step.action.path) {
          // For navigation: set spotlight immediately (target already exists)
          if (step.action?.spotlightId) {
            setSpotlight(step.action.spotlightId);
          }
          navigate(step.action.path);
        }
      };

      // If not on a hangar page, navigate to user's sandbox hangar then execute action
      if (!isOnHangarPage && activeFBO?.id) {
        try {
          // Get user's sandbox hangar (RLS ensures only their sandbox is returned)
          const { data: sandboxHangars } = await postgrest
            .from('hangar')
            .select('id')
            .eq('fbo_id', activeFBO.id)
            .not('owner_user_id', 'is', null);

          if (sandboxHangars && sandboxHangars.length > 0) {
            navigate(`/hangars/${sandboxHangars[0].id}`);
            // Delay action to let hangar page render
            setTimeout(executeAction, 500);
            return;
          }
        } catch (err) {
          console.error('Failed to fetch sandbox hangar for activation:', err);
        }
      }

      // Already on hangar page or couldn't fetch - just execute
      executeAction();
    },
    [navigate, emitEvent, setSpotlight, isOnHangarPage, postgrest, activeFBO]
  );
}
